import React, { useState } from "react";
import { Link, useNavigate } from "react-router-dom";
import { api, arabicError } from "../lib/api";
import { toast } from "sonner";
import { UserPlus, CheckCircle2 } from "lucide-react";
import { SYRIAN_CITIES } from "../lib/constants";
import MaterialSelector from "../components/MaterialSelector";
import LocationPicker from "../components/LocationPicker";

const Register = () => {
  const navigate = useNavigate();
  const [form, setForm] = useState({
    first_name: "", last_name: "",
    email: "", password: "", confirm: "",
    store_name: "", city: SYRIAN_CITIES[0],
    detailed_address: "", phone_number: "",
    material_types: [],
    location: null,
  });
  const [loading, setLoading] = useState(false);
  const [done, setDone] = useState(null);

  const set = (k, v) => setForm((s) => ({ ...s, [k]: v }));

  const onSubmit = async (e) => {
    e.preventDefault();
    if (!form.first_name.trim() || !form.last_name.trim()) { toast.error("الاسم والكنية مطلوبان"); return; }
    if (form.password !== form.confirm) { toast.error("كلمتا المرور غير متطابقتين"); return; }
    if (form.password.length < 6) { toast.error("كلمة المرور يجب أن تكون 6 أحرف على الأقل"); return; }
    if (form.material_types.length === 0) { toast.error("اختر نوع المواد التي تتعامل بها (على الأقل نوع واحد)"); return; }
    if (!/^[+\d\s-]{4,}$/.test(form.phone_number)) { toast.error("رقم الهاتف غير صالح"); return; }
    if (!form.location || typeof form.location.lat !== "number") {
      toast.error("يجب تحديد الموقع على جوجل ماب");
      return;
    }
    setLoading(true);
    try {
      const r = await api.post("/auth/register", {
        first_name: form.first_name,
        last_name: form.last_name,
        email: form.email,
        password: form.password,
        store_name: form.store_name,
        city: form.city,
        detailed_address: form.detailed_address,
        phone_number: form.phone_number,
        material_types: form.material_types,
        location: form.location,
      });
      setDone({ email: form.email, reference: r.data.reference, message: r.data.message });
      toast.success("تم إنشاء حسابك بنجاح");
    } catch (err) { toast.error(arabicError(err)); } finally { setLoading(false); }
  };

  const inputCls = "h-12 w-full rounded-lg border border-[#7AB2B2] bg-white px-4 focus:outline-none focus:ring-2 focus:ring-[#09637E]";

  if (done) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-[#EBF4F6] p-6" dir="rtl">
        <div className="bg-white border border-[#7AB2B2] rounded-2xl p-8 w-full max-w-lg text-center" data-testid="register-success">
          <div className="w-16 h-16 mx-auto rounded-2xl bg-[#EBF4F6] text-[#09637E] flex items-center justify-center mb-4"><CheckCircle2 size={36} /></div>
          <h2 className="text-2xl font-extrabold text-[#333333] mb-2">تم إنشاء حسابك بنجاح</h2>
          <p className="text-[#666666] mb-4">{done.message}</p>
          {done.reference && (
            <div className="bg-[#EBF4F6] border border-[#7AB2B2] rounded-xl p-4 mb-5">
              <div className="text-xs text-[#666666] mb-1">الرقم المرجعي لطلبك</div>
              <div className="text-2xl font-extrabold text-[#09637E] font-mono" dir="ltr" data-testid="register-reference">{done.reference}</div>
            </div>
          )}
          <p className="text-sm text-[#666666] mb-6">سيصلك تفعيل الحساب من الإدارة، وبعدها يمكنك تسجيل الدخول بالبريد: <b dir="ltr">{done.email}</b></p>
          <button onClick={() => navigate("/login")} data-testid="register-go-login"
            className="h-12 w-full rounded-lg bg-[#09637E] hover:bg-[#088395] text-white font-extrabold">الانتقال إلى تسجيل الدخول</button>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen flex items-stretch bg-[#EBF4F6]" dir="rtl">
      <div className="hidden lg:block w-1/2 relative">
        <img src="https://images.pexels.com/photos/36756015/pexels-photo-36756015.jpeg?auto=compress&cs=tinysrgb&dpr=2&h=1024&w=1024"
          alt="market" className="absolute inset-0 w-full h-full object-cover" />
        <div className="absolute inset-0 bg-gradient-to-l from-[#09637E]/85 to-[#09637E]/40" />
        <div className="absolute inset-0 flex flex-col justify-end p-12 text-white">
          <h1 className="text-5xl font-extrabold mb-4">انضم إلى إمداد</h1>
          <p className="text-xl opacity-90 max-w-lg">كن جزءاً من شبكة تجار السيراميك في سوريا.</p>
        </div>
      </div>

      <div className="flex-1 flex items-center justify-center p-6 sm:p-10">
        <div className="w-full max-w-lg">
          <div className="bg-white border border-[#7AB2B2] rounded-2xl p-8">
            <h2 className="text-3xl font-extrabold text-[#333333] mb-2">تسجيل تاجر جديد</h2>
            <p className="text-[#666666] mb-6">ستتم مراجعة طلبك من قبل الإدارة</p>

            <form onSubmit={onSubmit} className="space-y-4" data-testid="register-form">
              <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                <div>
                  <label className="block text-sm font-bold text-[#333333] mb-1">الاسم *</label>
                  <input type="text" required value={form.first_name} onChange={(e) => set("first_name", e.target.value)}
                    data-testid="register-first-name-input" className={inputCls} />
                </div>
                <div>
                  <label className="block text-sm font-bold text-[#333333] mb-1">الكنية *</label>
                  <input type="text" required value={form.last_name} onChange={(e) => set("last_name", e.target.value)}
                    data-testid="register-last-name-input" className={inputCls} />
                </div>
                <div>
                  <label className="block text-sm font-bold text-[#333333] mb-1">البريد الإلكتروني *</label>
                  <input type="email" required value={form.email} onChange={(e) => set("email", e.target.value)}
                    data-testid="register-email-input" className={inputCls} placeholder="example@imdad.com" />
                </div>
                <div>
                  <label className="block text-sm font-bold text-[#333333] mb-1">رقم الهاتف *</label>
                  <input type="tel" required value={form.phone_number} onChange={(e) => set("phone_number", e.target.value)}
                    data-testid="register-phone-input" className={inputCls} placeholder="09xxxxxxxx" />
                </div>
                <div>
                  <label className="block text-sm font-bold text-[#333333] mb-1">كلمة المرور *</label>
                  <input type="password" required value={form.password} onChange={(e) => set("password", e.target.value)}
                    data-testid="register-password-input" className={inputCls} />
                </div>
                <div>
                  <label className="block text-sm font-bold text-[#333333] mb-1">تأكيد كلمة المرور *</label>
                  <input type="password" required value={form.confirm} onChange={(e) => set("confirm", e.target.value)}
                    data-testid="register-confirm-input" className={inputCls} />
                </div>
                <div>
                  <label className="block text-sm font-bold text-[#333333] mb-1">اسم المحل *</label>
                  <input type="text" required value={form.store_name} onChange={(e) => set("store_name", e.target.value)}
                    data-testid="register-store-input" className={inputCls} />
                </div>
                <div>
                  <label className="block text-sm font-bold text-[#333333] mb-1">المحافظة *</label>
                  <select required value={form.city} onChange={(e) => set("city", e.target.value)}
                    data-testid="register-city-input" className={inputCls}>
                    {SYRIAN_CITIES.map((c) => <option key={c} value={c}>{c}</option>)}
                  </select>
                </div>
              </div>

              <div>
                <label className="block text-sm font-bold text-[#333333] mb-1">العنوان التفصيلي</label>
                <textarea value={form.detailed_address} onChange={(e) => set("detailed_address", e.target.value)}
                  data-testid="register-address-input" rows={2} placeholder="الشارع، البناء، رقم المحل..."
                  className="w-full rounded-lg border border-[#7AB2B2] bg-white px-4 py-2 focus:outline-none focus:ring-2 focus:ring-[#09637E]" />
              </div>

              <div>
                <label className="block text-sm font-bold text-[#333333] mb-2">الموقع على الخريطة *</label>
                <LocationPicker value={form.location} onChange={(v) => set("location", v)} testidPrefix="register-loc" />
                {!form.location && (
                  <p className="text-xs text-red-500 mt-1">مطلوب — اضغط الزر وحدّد موقع محلّك</p>
                )}
              </div>

              <div>
                <label className="block text-sm font-bold text-[#333333] mb-2">أنواع المواد التي تتعامل بها *</label>
                <MaterialSelector value={form.material_types} onChange={(v) => set("material_types", v)} testidPrefix="material" />
                {form.material_types.length === 0 && (
                  <p className="text-xs text-[#666666] mt-2">اختر نوعاً واحداً على الأقل (أو اضغط "أخرى" لإضافة نوع غير مدرج)</p>
                )}
              </div>

              <button type="submit" disabled={loading} data-testid="register-submit-btn"
                className="h-12 w-full rounded-lg bg-[#09637E] hover:bg-[#088395] text-white font-extrabold flex items-center justify-center gap-2 disabled:opacity-60">
                <UserPlus size={18} />
                {loading ? "جارٍ الإرسال..." : "تسجيل"}
              </button>
            </form>

            <div className="mt-6 text-center text-[#666666]">
              لديك حساب؟{" "}
              <Link to="/login" data-testid="go-to-login" className="inline-flex items-center min-h-[44px] px-2 text-[#09637E] font-bold hover:underline">سجّل الدخول</Link>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

export default Register;
